// app/_shims/semantic-ui-react.jsx
// Server-safe, static-only shims (OK in RSC)
// These render correct HTML with Semantic UI class names but ship NO JavaScript

import { styles as HeaderStyles } from "../../components/Header/element";

function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

// ============================================================================
// GRID COMPONENTS
// ============================================================================

export function Grid({
  stackable,
  divided,
  doubling,
  padded,
  relaxed,
  className,
  children,
  columns,
  ...rest
}) {
  const dividedCls =
    divided === "vertically"
      ? "vertically divided"
      : divided === "horizontally"
      ? "horizontally divided"
      : divided
      ? "divided"
      : "";
  const paddedCls =
    padded === "vertically"
      ? "vertically padded"
      : padded === "horizontally"
      ? "horizontally padded"
      : padded
      ? "padded"
      : "";
  const relaxedCls = relaxed === "very" ? "very relaxed" : relaxed ? "relaxed" : "";

  return (
    <div
      className={cx(
        "ui grid",
        stackable && "stackable",
        doubling && "doubling",
        dividedCls,
        paddedCls,
        relaxedCls,
        columns && `${widthWords[columns]} column`,
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

const widthWords = [
  "zero", "one", "two", "three", "four", "five", "six", "seven",
  "eight", "nine", "ten", "eleven", "twelve", "thirteen",
  "fourteen", "fifteen", "sixteen"
];

Grid.Row = function Row({
  columns,
  centered,
  stretched,
  reversed,
  divided,
  only,
  textAlign,
  verticalAlign,
  className,
  children,
  ...rest
}) {
  const reversedCls = reversed === "computer"
    ? "computer reversed"
    : reversed === "tablet"
    ? "tablet reversed"
    : reversed === "mobile"
    ? "mobile reversed"
    : reversed
    ? "reversed"
    : "";

  const onlyCls = only === "computer"
    ? "computer only"
    : only === "tablet"
    ? "tablet only"
    : only === "mobile"
    ? "mobile only"
    : only === "large screen"
    ? "large screen only"
    : only === "widescreen"
    ? "widescreen only"
    : "";

  return (
    <div
      className={cx(
        columns && `${widthWords[columns]} column`,
        centered && "centered",
        stretched && "stretched",
        divided && "divided",
        reversedCls,
        onlyCls,
        textAlign && `${textAlign} aligned`,
        verticalAlign && `${verticalAlign} aligned`,
        "row",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

Grid.Column = function Column({
  width,
  mobile,
  tablet,
  computer,
  largeScreen,
  widescreen,
  stretched,
  floated,
  textAlign,
  verticalAlign,
  only,
  className,
  children,
  ...rest
}) {
  // Standard width
  const widthClass = width ? `${widthWords[width]} wide` : "";

  // Responsive widths (mobile={16} tablet={8} computer={4})
  const mobileClass = mobile ? `${widthWords[mobile]} wide mobile` : "";
  const tabletClass = tablet ? `${widthWords[tablet]} wide tablet` : "";
  const computerClass = computer ? `${widthWords[computer]} wide computer` : "";
  const largeScreenClass = largeScreen ? `${widthWords[largeScreen]} wide large screen` : "";
  const widescreenClass = widescreen ? `${widthWords[widescreen]} wide widescreen` : "";

  // Only show on specific devices
  const onlyCls = only === "computer"
    ? "computer only"
    : only === "tablet"
    ? "tablet only"
    : only === "mobile"
    ? "mobile only"
    : only === "large screen"
    ? "large screen only"
    : only === "widescreen"
    ? "widescreen only"
    : "";

  return (
    <div
      className={cx(
        widthClass,
        mobileClass,
        tabletClass,
        computerClass,
        largeScreenClass,
        widescreenClass,
        stretched && "stretched",
        floated && `${floated} floated`,
        textAlign && `${textAlign} aligned`,
        verticalAlign && `${verticalAlign} aligned`,
        onlyCls,
        "column",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

// ============================================================================
// SEGMENT
// ============================================================================

export function Segment({
  basic,
  raised,
  stacked,
  piled,
  vertical,
  disabled,
  loading,
  inverted,
  padded,
  compact,
  circular,
  clearing,
  floated,
  textAlign,
  attached,
  color,
  size,
  secondary,
  tertiary,
  placeholder,
  className,
  children,
  ...rest
}) {
  const paddedCls =
    padded === "very"
      ? "very padded"
      : padded
      ? "padded"
      : "";

  const attachedCls =
    attached === "top"
      ? "top attached"
      : attached === "bottom"
      ? "bottom attached"
      : attached
      ? "attached"
      : "";

  return (
    <div
      className={cx(
        "ui",
        basic && "basic",
        raised && "raised",
        stacked && "stacked",
        piled && "piled",
        vertical && "vertical",
        disabled && "disabled",
        loading && "loading",
        inverted && "inverted",
        paddedCls,
        compact && "compact",
        circular && "circular",
        clearing && "clearing",
        floated && `${floated} floated`,
        textAlign && `${textAlign} aligned`,
        attachedCls,
        color,
        size,
        secondary && "secondary",
        tertiary && "tertiary",
        placeholder && "placeholder",
        "segment",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

Segment.Group = function SegmentGroup({
  horizontal,
  raised,
  stacked,
  piled,
  compact,
  className,
  children,
  ...rest
}) {
  return (
    <div
      className={cx(
        "ui",
        horizontal && "horizontal",
        raised && "raised",
        stacked && "stacked",
        piled && "piled",
        compact && "compact",
        "segments",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

Segment.Inline = function SegmentInline({ className, children, ...rest }) {
  return (
    <div className={cx("ui inline segment", className)} {...rest}>
      {children}
    </div>
  );
};

// ============================================================================
// HEADER
// ============================================================================

export function Header({
  as = "div",
  name,
  content,
  className,
  colorClass,
  size = "medium",
  sub,
  dividing,
  floated,
  children,
  ...rest
}) {
  const Comp = as;
  return (
    <Comp
      className={cx(
        "ui",
        size && size,
        sub && "sub",
        dividing && "dividing",
        floated && `${floated} floated`,
        "header",
        className
      )}
      style={colorClass ? HeaderStyles.black : HeaderStyles.normal}
      {...rest}
    >
      {name ? name : null}
      {content ? content : null}
      {children}
    </Comp>
  );
}

Header.Content = function HeaderContent({ children, className, ...rest }) {
  return (
    <div className={cx("content", className)} {...rest}>
      {children}
    </div>
  );
};

Header.Subheader = function Subheader({ children, className, ...rest }) {
  return (
    <div className={cx("sub header", className)} {...rest}>
      {children}
    </div>
  );
};

// ============================================================================
// DIVIDER
// ============================================================================

export function Title({
  as = "div",
  name,
  className,
  colorClass,
  size = "medium",
  sub,
  dividing,
  floated,
  children,
  ...rest
}) {
  const Comp = as;
  return (
    <Comp
      className={cx(
        "ui",
        size && size,
        sub && "sub",
        dividing && "dividing",
        floated && `${floated} floated`,
        "header",
        className
      )}
      style={colorClass ? HeaderStyles.black : HeaderStyles.normal}
      {...rest}
    >
      {name ? name : null}
      {children}
    </Comp>
  );
}

// ============================================================================
// DIVIDER
// ============================================================================

export function Divider({
  horizontal,
  vertical,
  hidden,
  clearing,
  fitted,
  className,
  children,
  ...rest
}) {
  return (
    <div
      className={cx(
        "ui",
        horizontal && "horizontal",
        vertical && "vertical",
        hidden && "hidden",
        clearing && "clearing",
        fitted && "fitted",
        "divider",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

// ============================================================================
// LABEL
// ============================================================================

export function Label({
  as,
  basic,
  circular,
  color,
  content,
  pointing,
  ribbon,
  size,
  tag,
  className,
  children,
  ...rest
}) {
  const Component = as || "div";
  const pointingCls = pointing === true
    ? "pointing"
    : typeof pointing === "string"
    ? `${pointing} pointing`
    : "";
  const ribbonCls = ribbon === "right" ? "right ribbon" : ribbon ? "ribbon" : "";

  return (
    <Component
      className={cx(
        "ui",
        basic && "basic",
        circular && "circular",
        color,
        pointingCls,
        ribbonCls,
        size,
        tag && "tag",
        "label",
        className
      )}
      {...rest}
    >
      {content || children}
    </Component>
  );
}

// ============================================================================
// CONTAINER
// ============================================================================

export function Container({
  fluid,
  text,
  textAlign,
  className,
  children,
  ...rest
}) {
  return (
    <div
      className={cx(
        "ui",
        fluid && "fluid",
        text && "text",
        textAlign && textAlign,
        "container",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

// ============================================================================
// IMAGE
// ============================================================================

export function Image({
  className,
  ui = true,
  fluid,
  rounded,
  circular,
  centered,
  size,
  ...rest
}) {
  return (
    <img
      className={cx(
        ui ? "ui" : undefined,
        fluid && "fluid",
        rounded && "rounded",
        circular && "circular",
        centered && "centered",
        size,
        ui ? "image" : undefined,
        className
      )}
      {...rest}
    />
  );
}

// ============================================================================
// BUTTON (Static only - no onClick handlers in server components)
// ============================================================================

export function Button({
  className,
  basic,
  primary,
  secondary,
  positive,
  negative,
  fluid,
  compact,
  circular,
  icon,
  labeled,
  color,
  size,
  children,
  ...rest
}) {
  const classes = cx(
    "ui",
    basic && "basic",
    primary && "primary",
    secondary && "secondary",
    positive && "positive",
    negative && "negative",
    fluid && "fluid",
    compact && "compact",
    circular && "circular",
    icon && "icon",
    labeled && "labeled",
    color,
    size,
    "button",
    className
  );
  return (
    <button className={classes} {...rest}>
      {children}
    </button>
  );
}

// ============================================================================
// ICON
// ============================================================================

export function Icon({
  className,
  name,
  size,
  color,
  disabled,
  loading,
  fitted,
  link,
  circular,
  bordered,
  inverted,
  ...rest
}) {
  return (
    <i
      className={cx(
        name,
        size,
        color,
        disabled && "disabled",
        loading && "loading",
        fitted && "fitted",
        link && "link",
        circular && "circular",
        bordered && "bordered",
        inverted && "inverted",
        "icon",
        className
      )}
      {...rest}
    />
  );
}

// ============================================================================
// TABLE
// ============================================================================

export function Table({
  className,
  celled,
  basic,
  compact,
  unstackable,
  striped,
  singleLine,
  fixed,
  selectable,
  padded,
  collapsing,
  inverted,
  definition,
  structured,
  children,
  ...rest
}) {
  const classes = cx(
    "ui",
    celled && "celled",
    basic === true ? "basic" : basic === "very" ? "very basic" : undefined,
    compact === true ? "compact" : compact === "very" ? "very compact" : undefined,
    padded === true ? "padded" : padded === "very" ? "very padded" : undefined,
    unstackable && "unstackable",
    striped && "striped",
    singleLine && "single line",
    fixed && "fixed",
    selectable && "selectable",
    collapsing && "collapsing",
    inverted && "inverted",
    definition && "definition",
    structured && "structured",
    "table",
    className
  );
  return (
    <table className={classes} {...rest}>
      {children}
    </table>
  );
}

Table.Header = function THead(props) {
  return <thead {...props} />;
};

Table.Body = function TBody(props) {
  return <tbody {...props} />;
};

Table.Footer = function TFoot(props) {
  return <tfoot {...props} />;
};

Table.Row = function TRow({
  className,
  positive,
  negative,
  error,
  warning,
  active,
  disabled,
  ...rest
}) {
  return (
    <tr
      className={cx(
        positive && "positive",
        negative && "negative",
        error && "error",
        warning && "warning",
        active && "active",
        disabled && "disabled",
        className
      )}
      {...rest}
    />
  );
};

Table.HeaderCell = function TH({
  className,
  sorted,
  ...rest
}) {
  return (
    <th
      className={cx(
        sorted && `sorted ${sorted}`,
        className
      )}
      {...rest}
    />
  );
};

Table.Cell = function TD({
  className,
  positive,
  negative,
  error,
  warning,
  selectable,
  collapsing,
  textAlign,
  ...rest
}) {
  return (
    <td
      className={cx(
        positive && "positive",
        negative && "negative",
        error && "error",
        warning && "warning",
        selectable && "selectable",
        collapsing && "collapsing",
        textAlign && textAlign,
        className
      )}
      {...rest}
    />
  );
};

// ============================================================================
// LIST
// ============================================================================

export function List({
  bulleted,
  ordered,
  divided,
  relaxed,
  horizontal,
  selection,
  animated,
  size,
  children,
  className,
  ...rest
}) {
  const Tag = ordered ? "ol" : bulleted ? "ul" : "div";
  const relaxedCls = relaxed === "very" ? "very relaxed" : relaxed ? "relaxed" : "";

  return (
    <Tag
      className={cx(
        "ui list",
        bulleted && "bulleted",
        ordered && "ordered",
        divided && "divided",
        horizontal && "horizontal",
        selection && "selection",
        animated && "animated",
        size,
        relaxedCls,
        className
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}

List.Item = function ListItem({ children, className, ...rest }) {
  return <div className={cx("item", className)} {...rest}>{children}</div>;
};

List.Icon = function ListIcon({ name, className, ...rest }) {
  return <Icon name={name} className={cx(className)} {...rest} />;
};

List.Content = function ListContent({ children, className, ...rest }) {
  return <div className={cx("content", className)} {...rest}>{children}</div>;
};

List.Header = function ListHeader({ children, className, ...rest }) {
  return <div className={cx("header", className)} {...rest}>{children}</div>;
};

List.Description = function ListDescription({ children, className, ...rest }) {
  return <div className={cx("description", className)} {...rest}>{children}</div>;
};

// ============================================================================
// CARD
// ============================================================================

export function Card({
  fluid,
  link,
  centered,
  raised,
  children,
  className,
  href,
  ...rest
}) {
  const Tag = href ? "a" : "div";
  return (
    <Tag
      className={cx(
        "ui card",
        fluid && "fluid",
        link && "link",
        centered && "centered",
        raised && "raised",
        className
      )}
      {...(href ? { href } : {})}
      {...rest}
    >
      {children}
    </Tag>
  );
}

Card.Content = function CardContent({
  extra,
  children,
  className,
  ...rest
}) {
  return (
    <div
      className={cx(
        extra ? "extra content" : "content",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

Card.Header = function CardHeader({ children, className, ...rest }) {
  return <div className={cx("header", className)} {...rest}>{children}</div>;
};

Card.Meta = function CardMeta({ children, className, ...rest }) {
  return <div className={cx("meta", className)} {...rest}>{children}</div>;
};

Card.Description = function CardDescription({ children, className, ...rest }) {
  return <div className={cx("description", className)} {...rest}>{children}</div>;
};

// ============================================================================
// MENU (Static version - for navigation without state)
// ============================================================================

export function Menu({
  vertical,
  secondary,
  pointing,
  tabular,
  text,
  pagination,
  fluid,
  compact,
  size,
  children,
  className,
  ...rest
}) {
  return (
    <div
      className={cx(
        "ui",
        vertical && "vertical",
        secondary && "secondary",
        pointing && "pointing",
        tabular && "tabular",
        text && "text",
        pagination && "pagination",
        fluid && "fluid",
        compact && "compact",
        size,
        "menu",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

Menu.Item = function MenuItem({
  active,
  fitted,
  header,
  link,
  children,
  className,
  ...rest
}) {
  return (
    <div
      className={cx(
        active && "active",
        fitted && "fitted",
        header && "header",
        link && "link",
        "item",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

Menu.Menu = function SubMenu({ children, className, ...rest }) {
  return <div className={cx("menu", className)} {...rest}>{children}</div>;
};

Menu.Header = function MenuHeader({ children, className, ...rest }) {
  return <div className={cx("header", className)} {...rest}>{children}</div>;
};

// ============================================================================
// POPUP (Static version - renders tooltip using title attribute)
// ============================================================================

export function Popup({
  trigger,
  content,
  children,
  position = "top center",
  size,
  className,
  on,
  hoverable,
  ...rest
}) {
  // For server components, we render the trigger with a title attribute
  // The actual popup content is rendered as children inside a hidden span
  const popupContent = content || children;

  return (
    <span className={cx("ui-popup-wrapper", className)} {...rest}>
      {trigger}
      {popupContent && (
        <span className="ui-popup-content" style={{ display: 'none' }}>
          {popupContent}
        </span>
      )}
    </span>
  );
}

// ============================================================================
// TAB (Server-side version - renders first pane only, needs client wrapper for interactivity)
// ============================================================================

export function Tab({
  menu = {},
  panes = [],
  activeIndex = 0,
  className,
  defaultActiveIndex,
  renderActiveOnly = true,
  ...rest
}) {
  // For server rendering, show only the first pane content
  // This will be enhanced on the client side with interactivity
  const activePaneIndex = activeIndex ?? defaultActiveIndex ?? 0;
  const activePane = panes[activePaneIndex];

  const { text, attached } = menu;

  return (
    <div className={cx("ui-tab-wrapper", className)} {...rest}>
      {/* Render tab menu */}
      <div
        className={cx(
          "ui",
          text && "text",
          attached && attached === "top" ? "top attached" : attached === "bottom" ? "bottom attached" : "attached",
          "tabular menu"
        )}
      >
        {panes.map((pane, index) => (
          <div
            key={index}
            className={cx(
              "item",
              index === activePaneIndex && "active"
            )}
          >
            {pane.menuItem}
          </div>
        ))}
      </div>

      {/* Render active pane content only for SSR */}
      {activePane && (
        <div className={cx("ui", attached && attached === "bottom" ? "bottom attached" : "attached", "segment", "active tab")}>
          {typeof activePane.render === 'function' ? activePane.render() : activePane.pane}
        </div>
      )}
    </div>
  );
}
